import { useEffect, useState } from 'react';
import { ArrowUpRight, X } from 'lucide-react';
import { useSite } from '../lib/SiteContentContext';
import { BOOK_TRIAL_URL } from '../lib/config';

/**
 * Sticky bottom "Book a free trial" banner - site-wide, dismissable per session.
 */
export default function BookTrialBanner() {
  const { CTA, siteSettings } = useSite();
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    setDismissed(sessionStorage.getItem('ss_banner_dismissed') === '1');
  }, []);

  const hide = () => {
    sessionStorage.setItem('ss_banner_dismissed', '1');
    setDismissed(true);
  };

  if (dismissed) return null;
  if (siteSettings && siteSettings.bannerEnabled === false) return null;

  const bannerText =
    siteSettings?.bannerText || 'Ready to boost your grade? Book a free trial.';
  const bannerSubtext =
    siteSettings?.bannerSubtext || 'Speak to a qualified teacher. No obligation.';
  const ctaUrl = siteSettings?.ctaPrimaryUrl || BOOK_TRIAL_URL;
  const ctaLabel = siteSettings?.ctaPrimaryLabel || CTA?.primary || 'Book a free trial';

  return (
    <div
      role="region"
      aria-label="Book a free trial"
      className="fixed bottom-0 inset-x-0 z-40 pointer-events-none"
      data-testid="book-trial-banner"
    >
      <div className="pointer-events-auto bg-brand-sun text-brand-ink border-t-2 border-brand-ink shadow-[0_-8px_24px_-8px_rgba(0,0,0,0.25)]">
        <div className="container flex items-center gap-3 py-2.5 md:py-3">
          <div className="flex-1 min-w-0">
            <div className="font-display text-base sm:text-xl md:text-2xl leading-tight text-brand-ink tracking-wide uppercase">
              {bannerText}
            </div>
            <div className="hidden md:block text-xs text-brand-ink/70 mt-0.5">
              {bannerSubtext}
            </div>
          </div>
          <a
            href={ctaUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 bg-brand-ink text-brand-sun hover:bg-black rounded-full px-3.5 md:px-6 py-2 md:py-2.5 font-semibold text-sm md:text-base whitespace-nowrap transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-ink focus-visible:ring-offset-2 focus-visible:ring-offset-brand-sun"
          >
            <span className="hidden xs:inline sm:inline">{ctaLabel}</span>
            <span className="sm:hidden">Book trial</span>
            <ArrowUpRight className="w-4 h-4" />
          </a>
          <button
            onClick={hide}
            aria-label="Dismiss banner"
            className="w-8 h-8 rounded-full hover:bg-brand-ink/10 grid place-items-center shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-ink"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
